import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { 
  Trophy, Clock, Users, Flame, Calendar, Play,
  Zap, Crown, Timer, Gift, Medal, Sparkles,
  ChefHat, ArrowRight, Eye, ThumbsUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { differenceInHours, differenceInMinutes } from 'date-fns';

const CHALLENGE_TYPES = [
  { id: 'all', labelKey: 'common.all', icon: Trophy },
  { id: 'live', labelKey: 'challenges.live', icon: Zap, color: 'text-red-500' },
  { id: 'voting', labelKey: 'challenges.voting', icon: ThumbsUp, color: 'text-blue-500' },
  { id: 'upcoming', labelKey: 'challenges.upcoming', icon: Calendar, color: 'text-yellow-500' },
  { id: 'completed', labelKey: 'challenges.completed', icon: Medal, color: 'text-gray-500' },
];

// TV Show Style Featured Challenge Card
const FeaturedChallengeCard = ({ challenge, navigate, t }) => {
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    const updateTime = () => {
      if (challenge.end_date) {
        const end = new Date(challenge.end_date);
        const now = new Date();
        const hours = differenceInHours(end, now);
        const minutes = differenceInMinutes(end, now) % 60;
        if (hours > 0) {
          setTimeLeft(`${hours}h ${minutes}m`);
        } else if (minutes > 0) {
          setTimeLeft(`${minutes}m`);
        } else {
          setTimeLeft(t('challenges.ended'));
        }
      }
    };
    updateTime();
    const interval = setInterval(updateTime, 60000);
    return () => clearInterval(interval);
  }, [challenge.end_date, t]);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="relative overflow-hidden rounded-3xl cursor-pointer group"
      onClick={() => navigate(`/challenge/${challenge.id}`)}
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={challenge.cover_image || 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=1200&q=80'}
          alt={challenge.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
      </div>

      {/* Live Badge */}
      {challenge.status === 'active' && (
        <div className="absolute top-6 left-6 flex items-center gap-2">
          <span className="relative flex h-4 w-4">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-4 w-4 bg-red-500"></span>
          </span>
          <Badge className="bg-red-500 text-white font-bold px-4 py-1 text-sm">
            {t('challenges.live')}
          </Badge>
        </div>
      )}

      {/* Prize */}
      {challenge.prize_amount > 0 && (
        <div className="absolute top-6 right-6">
          <div className="bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-black px-5 py-2 rounded-full font-bold flex items-center gap-2 shadow-lg">
            <Gift className="w-5 h-5" />
            {challenge.prize_amount} TND
          </div>
        </div>
      )}

      {/* Content */}
      <div className="relative min-h-[400px] md:min-h-[500px] flex flex-col justify-end p-8">
        {/* Stats Bar */}
        <div className="flex items-center gap-6 mb-4">
          <div className="flex items-center gap-2 text-white/80">
            <Users className="w-5 h-5" />
            <span className="font-medium">{challenge.participants_count || 0} {t('challenges.participants')}</span>
          </div>
          {challenge.status === 'active' && timeLeft && (
            <div className="flex items-center gap-2 text-[#E07A5F]">
              <Timer className="w-5 h-5" />
              <span className="font-bold">{timeLeft} {t('challenges.remaining')}</span>
            </div>
          )}
          {challenge.status === 'voting' && (
            <div className="flex items-center gap-2 text-blue-400">
              <ThumbsUp className="w-5 h-5" />
              <span className="font-bold">{challenge.votes_count || 0} {t('challenges.votes')}</span>
            </div>
          )}
        </div>

        {/* Title */}
        <h2 className="font-fraunces text-4xl md:text-5xl font-bold text-white mb-3 leading-tight">
          {challenge.title}
        </h2>
        <p className="text-white/80 text-lg mb-6 line-clamp-2 max-w-2xl">
          {challenge.description}
        </p>

        {/* Action Button */}
        <div className="flex items-center gap-4">
          <Button 
            size="lg" 
            className="bg-[#E07A5F] hover:bg-[#D9381E] text-white rounded-full px-8 py-6 text-lg font-bold shadow-xl"
          >
            {challenge.status === 'active' ? (
              <>
                <Play className="w-5 h-5 mr-2" />
                {t('challenges.participateNow')}
              </>
            ) : challenge.status === 'voting' ? (
              <>
                <ThumbsUp className="w-5 h-5 mr-2" />
                {t('challenges.vote')}
              </>
            ) : (
              <>
                <Eye className="w-5 h-5 mr-2" />
                {t('challenges.seeDetails')}
              </>
            )}
          </Button>
          <Button 
            size="lg" 
            variant="outline" 
            className="border-white/30 text-white hover:bg-white/10 rounded-full px-6"
          >
            <ArrowRight className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

// Challenge Card
const ChallengeCard = ({ challenge, navigate, index, t }) => {
  const getStatusBadge = () => {
    switch (challenge.status) {
      case 'active':
        return <Badge className="bg-green-500 text-white">{t('challenges.ongoing')}</Badge>;
      case 'voting':
        return <Badge className="bg-blue-500 text-white">{t('challenges.voting')}</Badge>;
      case 'upcoming':
        return <Badge className="bg-yellow-500 text-black">{t('challenges.upcoming')}</Badge>;
      case 'completed':
        return <Badge className="bg-gray-500 text-white">{t('challenges.completed')}</Badge>;
      default:
        return null;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      whileHover={{ y: -4 }}
      className="cursor-pointer"
      onClick={() => navigate(`/challenge/${challenge.id}`)}
    >
      <Card className="overflow-hidden rounded-2xl group">
        <div className="relative aspect-video">
          <img
            src={challenge.cover_image || 'https://images.unsplash.com/photo-1583637113765-1d46854b606f?w=600&q=80'}
            alt={challenge.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
          
          {/* Status Badge */}
          <div className="absolute top-3 left-3 flex items-center gap-2">
            {getStatusBadge()}
            {challenge.status === 'active' && (
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
              </span>
            )}
          </div>

          {/* Prize */}
          {challenge.prize_amount > 0 && (
            <div className="absolute top-3 right-3 bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-black px-3 py-1 rounded-full text-sm font-bold flex items-center gap-1">
              <Gift className="w-4 h-4" />
              {challenge.prize_amount} TND
            </div>
          )}
          
          {/* Content */}
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <h3 className="font-fraunces font-bold text-xl text-white mb-1">{challenge.title}</h3>
            <p className="text-white/70 text-sm line-clamp-1">{challenge.description}</p>
          </div>
        </div>
        
        <CardContent className="p-4 bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                {challenge.participants_count || 0}
              </span>
              {challenge.time_limit_minutes && (
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {challenge.time_limit_minutes}min
                </span>
              )}
              {challenge.barka_reward > 0 && (
                <span className="flex items-center gap-1 text-[#E07A5F]">
                  <Flame className="w-4 h-4" />
                  +{challenge.barka_reward}
                </span>
              )}
            </div>
            <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-[#1E4ED8] group-hover:translate-x-1 transition-all" />
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

// Winners Podium
const WinnersPodium = ({ winners }) => {
  if (!winners || winners.length === 0) return null;

  return (
    <div className="flex items-end justify-center gap-4">
      {/* 2nd Place */}
      {winners[1] && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-center"
        >
          <Avatar className="w-16 h-16 border-4 border-[#C0C0C0] shadow-lg mx-auto">
            <AvatarImage src={winners[1].profile_picture} />
            <AvatarFallback className="bg-[#C0C0C0] text-white">{winners[1].username?.[0]}</AvatarFallback>
          </Avatar>
          <div className="mt-2 bg-[#C0C0C0] w-20 h-16 rounded-t-lg flex items-center justify-center">
            <span className="text-white font-bold text-2xl">2</span>
          </div>
          <p className="text-sm font-medium mt-1">{winners[1].username}</p>
        </motion.div>
      )}

      {/* 1st Place */}
      {winners[0] && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-center -mt-4"
        >
          <div className="relative">
            <Crown className="w-8 h-8 text-[#FFD700] mx-auto mb-1" />
            <Avatar className="w-20 h-20 border-4 border-[#FFD700] shadow-lg mx-auto">
              <AvatarImage src={winners[0].profile_picture} />
              <AvatarFallback className="bg-[#FFD700] text-black">{winners[0].username?.[0]}</AvatarFallback>
            </Avatar>
          </div>
          <div className="mt-2 bg-gradient-to-t from-[#FFD700] to-[#FFA500] w-20 h-24 rounded-t-lg flex items-center justify-center">
            <span className="text-white font-bold text-3xl">1</span>
          </div>
          <p className="text-sm font-bold mt-1">{winners[0].username}</p>
        </motion.div>
      )}

      {/* 3rd Place */}
      {winners[2] && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-center"
        >
          <Avatar className="w-14 h-14 border-4 border-[#CD7F32] shadow-lg mx-auto">
            <AvatarImage src={winners[2].profile_picture} />
            <AvatarFallback className="bg-[#CD7F32] text-white">{winners[2].username?.[0]}</AvatarFallback>
          </Avatar>
          <div className="mt-2 bg-[#CD7F32] w-20 h-12 rounded-t-lg flex items-center justify-center">
            <span className="text-white font-bold text-xl">3</span>
          </div>
          <p className="text-sm font-medium mt-1">{winners[2].username}</p>
        </motion.div>
      )}
    </div>
  );
};

export default function ChallengePage() {
  const { api } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [challenges, setChallenges] = useState([]);
  const [featuredChallenge, setFeaturedChallenge] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [recentWinners, setRecentWinners] = useState([]);


  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [challengesRes, seasonsRes, leaderboardRes] = await Promise.all([
        api.get('/challenges'),
        api.get('/cultural/seasons'),
        api.get('/leaderboard?limit=3'),
      ]);
      
      const allChallenges = challengesRes.data?.challenges || challengesRes.data || [];
      setChallenges(allChallenges);
      
      // Set featured as the first active or upcoming challenge
      const featured = allChallenges.find(c => c.status === 'active') || 
                       allChallenges.find(c => c.status === 'voting') ||
                       allChallenges.find(c => c.status === 'upcoming') ||
                       allChallenges[0];
      setFeaturedChallenge(featured);
      
      setSeasons(seasonsRes.data?.seasons || []);
      setRecentWinners(leaderboardRes.data?.leaderboard || []);
    } catch (error) {
      console.error('Failed to fetch challenges:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredChallenges = challenges.filter(c => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'live') return c.status === 'active';
    if (activeFilter === 'voting') return c.status === 'voting';
    if (activeFilter === 'upcoming') return c.status === 'upcoming';
    if (activeFilter === 'completed') return c.status === 'completed';
    return true;
  });

  return (
    <div className="min-h-screen bg-[#0F0F0F]" data-testid="challenges-page">
      {/* Hero - TV Show Banner */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1E4ED8]/20 via-transparent to-[#E07A5F]/20" />
        
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <Badge className="bg-[#E07A5F] text-white mb-3">
                <Sparkles className="w-3 h-3 mr-1" />
                {t('challenges.masterChef')}
              </Badge>
              <h1 className="font-fraunces text-4xl md:text-5xl font-bold text-white mb-2">
                {t('challenges.title')}
              </h1>
              <p className="text-white/60">
                {t('challenges.subtitle')}
              </p>
            </div>
            <div className="hidden lg:block text-right">
              <p className="text-white/40 text-sm">{t('challenges.totalPrize')}</p>
              <p className="text-[#FFD700] text-4xl font-bold">
                {challenges.reduce((sum, c) => sum + (c.prize_amount || 0), 0)} TND
              </p>
            </div>
          </div>

          {/* Featured Challenge */}
          {loading ? (
            <Skeleton className="h-[500px] rounded-3xl" />
          ) : featuredChallenge ? (
            <FeaturedChallengeCard challenge={featuredChallenge} navigate={navigate} t={t} />
          ) : null}
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="bg-[#1A1A1A] border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-2 py-4 overflow-x-auto scrollbar-hide">
            {CHALLENGE_TYPES.map((type) => {
              const Icon = type.icon;
              const count = type.id === 'all' 
                ? challenges.length 
                : challenges.filter(c => 
                    type.id === 'live' ? c.status === 'active' :
                    type.id === 'voting' ? c.status === 'voting' :
                    type.id === 'upcoming' ? c.status === 'upcoming' :
                    type.id === 'completed' ? c.status === 'completed' : false
                  ).length;
              
              return (
                <Button
                  key={type.id}
                  variant={activeFilter === type.id ? 'default' : 'ghost'}
                  className={`rounded-full whitespace-nowrap ${
                    activeFilter === type.id 
                      ? 'bg-[#E07A5F] text-white' 
                      : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
                  }`}
                  onClick={() => setActiveFilter(type.id)}
                >
                  <Icon className={`w-4 h-4 mr-2 ${type.color || ''}`} />
                  {t(type.labelKey)}
                  {count > 0 && (
                    <span className="ml-2 bg-white/20 px-2 py-0.5 rounded-full text-xs">
                      {count}
                    </span>
                  )}
                </Button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Challenges Grid */}
          <div className="lg:col-span-2">
            <h2 className="font-fraunces text-2xl font-bold text-white mb-6">
              {activeFilter === 'all' ? t('challenges.allChallenges') : t(CHALLENGE_TYPES.find(type => type.id === activeFilter)?.labelKey || 'challenges.allChallenges')}
            </h2>
            
            {loading ? (
              <div className="grid md:grid-cols-2 gap-6">
                {[1, 2, 3, 4].map((i) => (
                  <Card key={i} className="overflow-hidden rounded-2xl bg-[#1A1A1A]">
                    <Skeleton className="aspect-video" />
                    <div className="p-4 space-y-2">
                      <Skeleton className="h-5 w-3/4" />
                      <Skeleton className="h-4 w-1/2" />
                    </div>
                  </Card>
                ))}
              </div>
            ) : filteredChallenges.length === 0 ? (
              <Card className="p-12 text-center rounded-2xl bg-[#1A1A1A] border-white/10">
                <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
                  <Trophy className="w-10 h-10 text-white/40" />
                </div>
                <h3 className="font-fraunces text-xl font-bold text-white mb-2">{t('challenges.noChallenge')}</h3>
                <p className="text-white/60">{t('challenges.comeBack')}</p>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {filteredChallenges.filter(c => c.id !== featuredChallenge?.id).map((challenge, index) => (
                  <ChallengeCard 
                    key={challenge.id} 
                    challenge={challenge} 
                    navigate={navigate}
                    index={index}
                    t={t}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Leaderboard CTA — always visible on mobile */}
            <Link
              to="/ranking"
              className="lg:hidden flex items-center justify-between p-4 rounded-2xl bg-gradient-to-br from-[#1A1A1A] to-[#2A2A2A] border border-white/10 hover:border-[#FFD700]/40 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#FFD700]/10 flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-[#FFD700]" />
                </div>
                <div>
                  <p className="font-semibold text-white text-sm">{t('ranking.title')}</p>
                  <p className="text-white/50 text-xs">{t('ranking.subtitle')}</p>
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-white/40" />
            </Link>

            {/* Winners Spotlight */}
            {recentWinners.length > 0 && (
              <Card className="p-6 rounded-2xl bg-gradient-to-br from-[#1A1A1A] to-[#2A2A2A] border-white/10">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-fraunces text-lg font-bold text-white">{t('challenges.hallOfFame')}</h3>
                  <Crown className="w-5 h-5 text-[#FFD700]" />
                </div>
                <WinnersPodium winners={recentWinners} />
                <Link
                  to="/ranking"
                  className="block text-center text-[#E07A5F] text-sm mt-4 hover:underline"
                >
                  {t('challenges.seeFullLeaderboard')} →
                </Link>
              </Card>
            )}

            {/* Seasons */}
            {seasons.length > 0 && (
              <Card className="p-6 rounded-2xl bg-[#1A1A1A] border-white/10">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-fraunces text-lg font-bold text-white">{t('seasons.title')}</h3>
                  <Calendar className="w-5 h-5 text-[#1E4ED8]" />
                </div>
                <div className="space-y-3">
                  {seasons.slice(0, 4).map((season) => (
                    <Link
                      key={season.id}
                      to={`/seasons/${season.id}`}
                      className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1E4ED8] to-[#E07A5F] flex items-center justify-center text-white font-bold">
                        {season.name[0]}
                      </div>
                      <div>
                        <p className="font-medium text-white">{season.name}</p>
                        <p className="text-white/50 text-xs">{season.name_ar}</p>
                      </div>
                    </Link>
                  ))}
                </div>
                <Link 
                  to="/seasons" 
                  className="block text-center text-[#1E4ED8] text-sm mt-4 hover:underline"
                >
                  Voir toutes les saisons →
                </Link>
              </Card>
            )}

            {/* CTA */}
            <Card className="p-6 rounded-2xl bg-gradient-to-br from-[#E07A5F] to-[#D9381E] text-white">
              <ChefHat className="w-12 h-12 mb-4" />
              <h3 className="font-fraunces text-xl font-bold mb-2">Deviens un Champion!</h3>
              <p className="text-white/80 text-sm mb-4">
                Participe aux défis, gagne des BARKA et construis ta réputation de chef!
              </p>
              <Button 
                className="w-full bg-white text-[#D9381E] hover:bg-white/90"
                onClick={() => navigate('/home')}
              >
                Commencer
              </Button>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
